/**
 * V17.523 (Phase 1): the menu as a list — Home, then each visible section as a collapsible
 * group (Rules 2, 3, 6). Used by the expanded desktop sidebar and by the phone drawer.
 *
 * - Every group control is a <button aria-expanded> (brief step 7).
 * - The active item has aria-current="page" (Rule 3's own match: prefix + #hash — so plain Link,
 *   not NavLink, whose exact-match aria-current ignores both); a CLOSED section holding it shows a blue dot.
 * - Section headers have icons, items do not; labels wrap to two lines, then clamp with a
 *   tooltip (desktop) — never a one-line Lao ellipsis.
 */
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HOME, type NavItem } from '../../app/navigation';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { NAV_ICONS } from './icons';
import type { VisibleSection } from './navModel';
import type { NavLang } from './navStrings';

interface SectionListProps {
  /** V17.525 (row 87): the user's own landing page (resolveDefaultRoute) and whether it is the current page. */
  homePath: string;
  homeActive: boolean;
  sections: VisibleSection[];
  open: Set<string>;
  onToggle: (id: string) => void;
  activePath: string | null;
  activeSection: string | null;
  lang: NavLang;
  /** Phone: rows ≥44 px and no hover tooltips. */
  touch?: boolean;
  /** Phone (B8): tapping an item navigates AND closes the drawer. */
  onNavigate?: () => void;
}

function ItemLabel({ item, lang, touch }: { item: NavItem | typeof HOME; lang: NavLang; touch?: boolean }) {
  const text = item.label[lang];
  const span = <span className="line-clamp-2 break-words text-left leading-snug">{text}</span>;
  const hint = 'hint' in item && item.hint ? item.hint[lang] : null;
  if (touch) return span;
  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>{span}</TooltipTrigger>
      <TooltipContent side="right">
        <p className="font-medium">{text}</p>
        {hint && <p className="mt-0.5 text-sidebar-muted">{hint}</p>}
      </TooltipContent>
    </Tooltip>
  );
}

const rowBase =
  'flex w-full items-center gap-3 rounded-lg px-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring';

export function SectionList({ homePath, homeActive, sections, open, onToggle, activePath, activeSection, lang, touch, onNavigate }: SectionListProps) {
  const rowH = touch ? 'min-h-[44px] py-2' : 'min-h-[36px] py-1.5';
  const HomeIcon = NAV_ICONS[HOME.icon];

  return (
    <div className="space-y-1">
      <Link
        to={homePath}
        onClick={onNavigate}
        aria-current={homeActive ? 'page' : undefined}
        className={cn(
          rowBase,
          rowH,
          'font-medium',
          homeActive ? 'bg-sidebar-active-bg text-sidebar-active-fg' : 'text-sidebar-strong hover:bg-sidebar-hover',
        )}
      >
        <HomeIcon className={cn('h-5 w-5 shrink-0', homeActive ? 'text-sidebar-active-fg' : 'text-sidebar-icon')} aria-hidden="true" />
        <ItemLabel item={HOME} lang={lang} touch={touch} />
      </Link>

      {sections.map(({ id, section, items }) => {
        const Icon = NAV_ICONS[section.icon];
        const isOpen = open.has(id);
        const holdsActive = activeSection === id;
        return (
          <Collapsible key={id} open={isOpen} onOpenChange={() => onToggle(id)}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                aria-expanded={isOpen}
                data-section={id}
                className={cn(rowBase, rowH, 'font-semibold text-sidebar-strong hover:bg-sidebar-hover')}
              >
                <Icon className="h-5 w-5 shrink-0 text-sidebar-icon" aria-hidden="true" />
                <span className="flex-1 text-left leading-snug">{section.label[lang]}</span>
                {!isOpen && holdsActive && (
                  <span data-testid="active-dot" className="h-2 w-2 shrink-0 rounded-full bg-sidebar-accent" aria-hidden="true" />
                )}
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 text-sidebar-icon transition-transform', isOpen && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="ml-5 mt-1 space-y-0.5 border-l border-sidebar-guide pl-3">
                {items.map((item) => {
                  const active = activePath === item.path;
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        onClick={onNavigate}
                        aria-current={active ? 'page' : undefined}
                        data-nav-path={item.path}
                        className={cn(
                          rowBase,
                          rowH,
                          active
                            ? 'bg-sidebar-active-bg font-medium text-sidebar-active-fg'
                            : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-strong',
                        )}
                      >
                        <ItemLabel item={item} lang={lang} touch={touch} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
